import React, { useState, useEffect, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Label from '../../../../../Label';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import {
  CARD_ELEMENT_TYPE,
  PHOTO_TYPE,
} from '../../../../../../../services/cards/constants';
import { getImageBasedResizedFrame } from '../../../CardDesignerCardElements/CardDesignerCardElementControls/components/CardDesignerCardElementResizeControls/helpers';
import Button from '../../../../../Button';
import TooltipButton from '../../../../../TooltipButton';
import UploadIcon from '../../../../../../icons/UploadIcon';
import LogoIcon from '../../../../../../icons/LogoIcon';
import useAccountLogo from './hooks/useAccountLogo';
import { MAX_Z_INDEX } from '../../../../helpers';
import { BASE_LOGO_FRAME } from './helpers';
import useAccountPrintAssets from '../../../../../../../apps/pro/views/Account/AccountPrintAssets/components/AccountPrintAsset/hooks/useAccountPrintAssets';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import useDebounce from '../../../../../../../hooks/useDebounce';
import FormGroup from '../../../../../FormGroup';

const propTypes = {
  className: PropTypes.string,
  cardBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onCardElementCreate: PropTypes.func.isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func.isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardDesignerLogoControls = ({
  className,
  cardBounds,
  element = {},
  onCardElementCreate,
  onCardElementUpdate,
  onCardElementDelete,
  onElementConfigurationChange,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const { cardElementConfiguration } = element;
  const fileInputRef = useRef();
  const [centerLogo, setCenterLogo] = useState(false);
  const { accountLogo } = useAccountLogo();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);

  const { printAssets, printAssetsTotal, setSearchQuery } =
    useAccountPrintAssets({
      printAssetType: PRINT_ASSET_TYPES.LOGO,
    });

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery]);

  // The idea here is to setup the frame to an appropriate size after initial creation
  useEffect(() => {
    if (cardElementConfiguration && centerLogo) {
      const { asset_image: assetImage } = cardElementConfiguration;
      const frame = getImageBasedResizedFrame(
        cardBounds,
        BASE_LOGO_FRAME,
        assetImage,
        true,
      );

      onCardElementUpdate({
        cardElement: element.cardElement,
        data: {
          frame,
        },
      });
      setCenterLogo(false);
    }
  }, [cardElementConfiguration]);

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      // Having a cardElementConfiguration means we're working with an actual element
      if (cardElementConfiguration) {
        onElementConfigurationChange({
          element,
          config: {
            is_editable: false,
          },
          elementConfig: {
            asset_image_id: assetImage.id,
            photo_type: PHOTO_TYPE.PHOTO,
          },
        });
      } else {
        setCenterLogo(true);
        await onCardElementCreate({
          cardElementType: CARD_ELEMENT_TYPE.PHOTO,
          cardElementConfiguration: {
            is_editable: false,
            asset_image_id: assetImage.id,
            photo_type: PHOTO_TYPE.PHOTO,
          },
          input: {
            frame: BASE_LOGO_FRAME,
            z_index: MAX_Z_INDEX,
          },
        });
      }
    },
  });

  const handlePrintAssetSelect = printAsset => {
    if (cardElementConfiguration) {
      onElementConfigurationChange({
        element,
        config: {
          is_editable: false,
        },
        elementConfig: {
          existing_asset_image_id: printAsset.asset_image.id,
          photo_type: PHOTO_TYPE.PHOTO,
        },
      });
    } else {
      setCenterLogo(true);
      onCardElementCreate({
        cardElementType: CARD_ELEMENT_TYPE.PHOTO,
        cardElementConfiguration: {
          is_editable: false,
          existing_asset_image_id: printAsset.asset_image.id,
          photo_type: PHOTO_TYPE.PHOTO,
        },
        input: {
          frame: BASE_LOGO_FRAME,
          z_index: MAX_Z_INDEX,
        },
      });
    }
  };

  return (
    <div className={classes}>
      <input
        id="circle-asset-image"
        type="file"
        accept="image/*"
        onChange={event => {
          const [file] = event.target.files;
          onUpload(file);
        }}
        ref={ref => {
          fileInputRef.current = ref;
        }}
        hidden
      />
      {cardElementConfiguration?.asset_image && (
        <div className="card-designer-logo-controls-logo-container">
          <Button
            btnStyle="reset"
            onClick={async () => {
              onUploadReset();
              await onCardElementDelete(element.cardElement.id);
            }}
          >
            &times;
          </Button>
          <img src={cardElementConfiguration.asset_image.url} alt="Logo" />
        </div>
      )}{' '}
      <Fragment>
        <Label id="account-logo" className="mt-3">
          Static Logo Library
        </Label>
        <Button
          btnBlock
          btnStyle="info"
          className="text-primary text-sm text-bold"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <Fragment>Saving...</Fragment>
          ) : (
            <Fragment>
              <UploadIcon size={12} color="#0d84fe" className="mr-1" />
              Upload a logo
            </Fragment>
          )}
        </Button>
        {printAssetsTotal > 0 && (
          <FormGroup
            id="query"
            name="query"
            label="Query"
            labelSrOnly
            className="mt-2"
            placeholder="Search your logos.."
            onChange={({ target }) => setQuery(target.value)}
            value={query}
          />
        )}
        <div className="card-designer-print-assets-container">
          {accountLogo && (
            <Button
              className="card-designer-print-assets-item"
              btnStyle="reset"
              onClick={() => {
                if (cardElementConfiguration) {
                  onElementConfigurationChange({
                    element,
                    config: {
                      is_editable: false,
                    },
                    elementConfig: {
                      existing_asset_image_id: accountLogo.id,
                      photo_type: PHOTO_TYPE.PHOTO,
                    },
                  });
                } else {
                  setCenterLogo(true);
                  onCardElementCreate({
                    cardElementType: CARD_ELEMENT_TYPE.PHOTO,
                    cardElementConfiguration: {
                      is_editable: false,
                      existing_asset_image_id: accountLogo.id,
                      photo_type: PHOTO_TYPE.PHOTO,
                    },
                    input: {
                      frame: BASE_LOGO_FRAME,
                      z_index: MAX_Z_INDEX,
                    },
                  });
                }
              }}
            >
              <img
                src={accountLogo.url}
                alt="Account Logo"
                className="card-designer-print-backgrounds-assets-image"
              />
            </Button>
          )}
          {printAssets.map(printAsset => (
            <Button
              className="card-designer-print-assets-item"
              btnStyle="reset"
              onClick={() => handlePrintAssetSelect(printAsset)}
            >
              <img
                src={printAsset.asset_image.url}
                alt={printAsset.name}
                className="card-designer-print-backgrounds-assets-image"
              />
            </Button>
          ))}
        </div>
      </Fragment>
      <Fragment>
        <Label id="logo-assets" className="mt-3">
          Add Dynamic Logo
        </Label>
        <div className="card-designer-dynamic-logo-container">
          <TooltipButton
            className="card-designer-print-assets-item custom-logo"
            btnStyle="reset"
            tooltipId="logo-placeholder-tooltip"
            tooltipProps={{
              place: 'right',
            }}
            tip={
              <>
                Adding a Dynamic Logo placeholder to
                <br />
                your custom print allows for dynamically
                <br />
                inserting unique logos into custom prints
                <br />
                by Gallery or Album. Simply add this element
                <br />
                to your custom print and then add unique logos
                <br />
                within Gallery or Album Settings &rarr; General.
                <br />
                If you add a Dynamic Logo but do not have a
                <br />
                logo in Gallery or Album Settings, no logo will
                <br />
                be added to your print.
              </>
            }
            onClick={() => {
              if (cardElementConfiguration) {
                onElementConfigurationChange({
                  element,
                  config: {
                    is_editable: true,
                  },
                  elementConfig: {
                    asset_image_id: null,
                    existing_asset_image_id: null,
                    photo_type: PHOTO_TYPE.LOGO,
                  },
                });
              } else {
                setCenterLogo(true);
                onCardElementCreate({
                  cardElementType: CARD_ELEMENT_TYPE.PHOTO,
                  cardElementConfiguration: {
                    is_editable: true,
                    photo_type: PHOTO_TYPE.LOGO,
                  },
                  input: {
                    frame: BASE_LOGO_FRAME,
                    z_index: MAX_Z_INDEX,
                  },
                });
              }
            }}
          >
            <LogoIcon color="#0d84fe" size={30} />
          </TooltipButton>
        </div>
      </Fragment>
    </div>
  );
};

CardDesignerLogoControls.propTypes = propTypes;

export default CardDesignerLogoControls;
